if (!customElements.get('nhra-newsletter')) {
  customElements.define(
    'nhra-newsletter',
    class NhraNewsletter extends HTMLElement {
      static EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      static KLAVIYO_REVISION = '2026-07-15';

      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[type="email"]');
        this.error = this.querySelector('.nhra-newsletter__error');
        this.button = this.querySelector('button[type="submit"]');
        this.successTemplate = this.querySelector('.nhra-newsletter__success-template');
        if (this.querySelector('[data-lead-captured]')) this.trackLead();
        if (!this.form || !this.input || !this.error) return;

        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.input.addEventListener('input', () => this.clearError());
      }

      get usesKlaviyo() {
        return Boolean(this.dataset.klaviyoKey && this.dataset.klaviyoList);
      }

      onSubmit(event) {
        const email = this.input.value.trim();
        this.input.value = email;

        if (!NhraNewsletter.EMAIL_PATTERN.test(email)) {
          event.preventDefault();
          this.showError(this.error.dataset.invalidMessage);
          return;
        }

        if (!this.usesKlaviyo) return;

        event.preventDefault();
        this.subscribeToKlaviyo(email);
      }

      async subscribeToKlaviyo(email) {
        if (this.pending) return;
        this.pending = true;
        this.setBusy(true);

        const url = `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.dataset.klaviyoKey)}`;
        const body = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: this.dataset.klaviyoSource,
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                  },
                },
              },
            },
            relationships: {
              list: { data: { type: 'list', id: this.dataset.klaviyoList } },
            },
          },
        };

        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              revision: NhraNewsletter.KLAVIYO_REVISION,
              'Content-Type': 'application/vnd.api+json',
            },
            body: JSON.stringify(body),
          });
          if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);
          this.showSuccess(email);
          this.trackLead();
        } catch (error) {
          console.error('[nhra-newsletter]', error);
          this.showError(this.error.dataset.genericMessage);
        } finally {
          this.pending = false;
          this.setBusy(false);
        }
      }

      showSuccess(email) {
        if (!this.successTemplate) return;
        const success = this.successTemplate.content.firstElementChild.cloneNode(true);
        const walker = document.createTreeWalker(success, NodeFilter.SHOW_TEXT);
        while (walker.nextNode()) {
          const node = walker.currentNode;
          if (node.nodeValue.includes('__NHRA_EMAIL__')) {
            node.nodeValue = node.nodeValue.replaceAll('__NHRA_EMAIL__', email);
          }
        }
        this.form.replaceChildren(success);
        success.focus();
      }

      trackLead() {
        const pixelId = this.dataset.fbPixel;
        if (!pixelId || !NhraNewsletter.marketingAllowed()) return;
        NhraNewsletter.loadPixel(pixelId);
        window.fbq('trackSingle', pixelId, 'Lead', {
          content_name: this.dataset.klaviyoSource || 'NHRA newsletter',
        });
      }

      static marketingAllowed() {
        const privacy = window.Shopify?.customerPrivacy;
        if (!privacy || typeof privacy.marketingAllowed !== 'function') return true;
        return privacy.marketingAllowed();
      }

      static loadPixel(pixelId) {
        if (!window.fbq) {
          const fbq = function () {
            fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
          };
          if (!window._fbq) window._fbq = fbq;
          fbq.push = fbq;
          fbq.loaded = true;
          fbq.version = '2.0';
          fbq.queue = [];
          window.fbq = fbq;

          const script = document.createElement('script');
          script.async = true;
          script.src = 'https://connect.facebook.net/en_US/fbevents.js';
          document.head.appendChild(script);
        }

        NhraNewsletter.initializedPixels ??= new Set();
        if (NhraNewsletter.initializedPixels.has(pixelId)) return;

        const activePixels = window.fbq.getState?.().pixels?.map((pixel) => pixel.id) ?? [];
        if (!activePixels.includes(pixelId)) window.fbq('init', pixelId);
        NhraNewsletter.initializedPixels.add(pixelId);
      }

      setBusy(busy) {
        this.form.toggleAttribute('aria-busy', busy);
        if (this.button) this.button.disabled = busy;
      }

      showError(message) {
        this.error.textContent = message;
        this.error.hidden = false;
        this.input.setAttribute('aria-invalid', 'true');
        this.input.focus();
      }

      clearError() {
        this.error.hidden = true;
        this.error.textContent = '';
        this.input.removeAttribute('aria-invalid');
      }
    }
  );
}
